import type { EntityStatusTone } from '../../../components/entity-detail/EntityStatusChip.js';
import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { useState, type ReactElement } from 'react';

import { Button, Empty, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import { useMeetings } from '../../meetings/hooks/useMeetings.js';
import { MeetingFormModal } from '../../meetings/components/MeetingFormModal.js';
import type { MeetingStatus } from '../../meetings/api/meetings.client.js';
import { formatDateTime } from '../../../shared/format/index.js';
import { i18n } from '../../../i18n/index.js';

interface ProjectMeetingsTabProps {
  projectId: string;
}

const MEETING_STATUS_LABEL: Record<MeetingStatus, string> = {
  scheduled: 'crm.features.features.projects.components.projectmeetingstab.запланирована',
  completed: 'crm.features.features.projects.components.projectmeetingstab.завершена',
  cancelled: 'crm.features.features.projects.components.projectmeetingstab.отменена',
};

const MEETING_STATUS_TONE: Record<MeetingStatus, EntityStatusTone> = {
  scheduled: 'processing',
  completed: 'success',
  cancelled: 'default',
};

export function ProjectMeetingsTab({ projectId }: ProjectMeetingsTabProps): ReactElement {
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading } = useMeetings({ projectId });
  const rows = data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-medium text-[var(--ant-color-text-secondary)]">
          {i18n.t('crm.features.features.projects.components.projectmeetingstab.встречи_проекта', {
            p0: rows.length,
          })}
        </span>
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}
          data-testid="project-meetings-create"
        >
          {i18n.t('crm.features.features.projects.components.projectmeetingstab.новая_встреча')}
        </Button>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={i18n.t(
            'crm.features.features.projects.components.projectmeetingstab.встреч_нет',
          )}
        />
      ) : (
        <div className="flex flex-col divide-y divide-[var(--ant-color-border-secondary)] rounded-lg border border-[var(--ant-color-border)] bg-white">
          {rows.map((meeting) => (
            <div key={meeting.id} className="flex items-center gap-3 px-4 py-2.5">
              <Link
                to={`/meetings/${meeting.id}`}
                className="min-w-0 flex-1 truncate text-[13px] font-medium text-[var(--ant-color-text-heading)] hover:text-[var(--ant-color-primary)]"
              >
                {meeting.title}
              </Link>
              <span className="text-xs text-[var(--ant-color-text-secondary)]">
                {formatDateTime(meeting.startsAt)}
              </span>
              <EntityStatusChip
                tone={MEETING_STATUS_TONE[meeting.status]}
                style={{ marginInlineEnd: 0 }}
              >
                {i18n.t(MEETING_STATUS_LABEL[meeting.status])}
              </EntityStatusChip>
            </div>
          ))}
        </div>
      )}
      {createOpen ? (
        <MeetingFormModal open lockedProjectId={projectId} onClose={() => setCreateOpen(false)} />
      ) : null}
    </div>
  );
}
